import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { UseFormRegister, UseFormWatch, UseFormSetValue, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { ItemDetailsProps } from './item-details.types';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../hooks/useRedux';
import { calculateInvoice, getTotalCalculation } from '../features/invoice/invoice.slice';
import { setSelectedFreelancersForInvoice } from '../features/customer/customer.slice';
import { InvoiceModals } from './InvoiceModals';
import { FreelancerCardContainer } from './FreelancerCardContainer';
import DefaultInvoiceSection from './DefaultInvoiceSection';
import ExpressPaymentSection from './ExpressPaymentSection';
import { Tooltip } from './common/tooltip.component';
import { useInvoiceCalculation } from '../hooks/useInvoiceCalculation.hook';
import { useFormInitialization } from '../hooks/useFormInitialization.hook';
import { useFreelancerCleanup } from '../hooks/useFreelancerCleanup.hook';
import { useExpressPaymentValidation } from '../hooks/useExpressPaymentValidation.hook';
import { useTableRowHandlers } from '../hooks/useTableRowHandlers.hook';
import { useDateSelection } from '../hooks/useDateSelection.hook';
import { useDeductionSubmit } from '../hooks/useDeductionSubmit.hook';
import { useCalculationBreakdown } from '../hooks/useCalculationBreakdown.hook';
import { useTableRowUpdates } from '../hooks/useTableRowUpdates.hook';
import { useTotalCalculation } from '../hooks/useTotalCalculation.hook';
import { useBenefitsManagement } from '../hooks/useBenefitsManagement.hook';
import { useFreelancerState } from '../hooks/useFreelancerState.hook';
import { useModalState } from '../hooks/useModalState.hook';
import { useDropdownState } from '../hooks/useDropdownState.hook';
import { useCustomerData } from '../hooks/useCustomerData.hook';
import { useWeekOffDaysFiltering } from '../hooks/useWeekOffDaysFiltering.hook';
import { useModalHandlers } from '../hooks/useModalHandlers.hook';
import { useInputChangeHandler } from '../hooks/useInputChangeHandler.hook';
import { useParentNotificationsAll } from '../hooks/useParentNotificationsAll.hook';
import { useTotalAmountCalculation } from '../hooks/useTotalAmountCalculation.hook';
import { useFreelancerExpansion } from '../hooks/useFreelancerExpansion.hook';
import { useDropdownOptions } from '../hooks/useDropdownOptions.hook';
import { usePricingType } from '../hooks/usePricingType.hook';
import { useEngagementType } from '../hooks/useEngagementType.hook';
import { createFieldValidator } from '../utils/fieldValidation.utils';
import { convertToDecimalHours, convertToTimeFormat } from '../utils/timeConversion.utils';
import { readCookie } from '../../common/FunctionUtils';
import './item-details.scss';

const ItemDetails: React.FC<ItemDetailsProps> = ({
  items = [],
  users = [],
  articles = [],
  currency = 'SEK',
  control,
  register,
  watch,
  setValue,
  errors,
  trigger,
  t: propT,
  globalData,
  onAddNewRow,
  onRemoveItem,
  showTooltipError = {},
  setShowTooltipError,
  preferences = {},
  calculatedAmounts,
  onChangeUnit,
  onChangeAmount,
  onServiceTableChange, // Add to destructured props
  onServiceTableDataByFreelancerChange, // NEW - Add to destructured props
  onPensionDataByFreelancerChange, // NEW - Add to destructured props
  onHolidayDataByFreelancerChange, // NEW - Add to destructured props
  onDeductionsByFreelancerChange, // NEW - Add to destructured props
  onRemovedCopiedWorkingHoursChange, // NEW - Add to destructured props
  onCalculatedSubAmountChange, // NEW - Add to destructured props
  copiedInvoiceData,  // NEW - Add to destructured props
  isEditModeInvoice,
  timesheetData,  // NEW - Add to destructured props
  preId,  // NEW - Add to destructured props
  validateServiceDescriptionsRef,
  createdDate,  // NEW - Add createdDate from URL
  invoiceNumber = 0, // NEW - Invoice number from URL for edit mode
  // formData,
}) => {
  // Get customer data using custom hook
  const { customerState, selectedCustomer, selectedFreelancersForInvoice } = useCustomerData();
  
  const authState = useAppSelector((state: any) => state?.auth || {});
  const { user } = authState;
  
  // Get BankID status from localStorage user object
  const localStorageUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : null;
  const isLoginWithBankId = localStorageUser?.isLoginWithBankId ? 1 : 0;
  
 
  const dispatch = useAppDispatch();
  const invoiceState = useAppSelector((state: any) => state?.invoice || {});
  const { calculationData } = invoiceState;
  const { t, i18n } = useTranslation();

  // Phase 4: Consolidated state management hooks
  const freelancerState = useFreelancerState();
  const modalState = useModalState();
  const dropdownState = useDropdownState();

  // Destructure freelancer state for easier access
  const {
    tableRowsByFreelancer, setTableRowsByFreelancer,
    serviceTableDataByFreelancer, setServiceTableDataByFreelancer,
    pensionDataByFreelancer, setPensionDataByFreelancer,
    holidayDataByFreelancer, setHolidayDataByFreelancer,
    deductionsByFreelancer, setDeductionsByFreelancer,
    calendarSelectedDatesByFreelancer, setCalendarSelectedDatesByFreelancer,
    lastUsedPricingTypeByFreelancer, setLastUsedPricingTypeByFreelancer,
    expandedSections, setExpandedSections,
    removedCopiedWorkingHours, setRemovedCopiedWorkingHours
  } = freelancerState;

  // Destructure modal state
  const {
    showCalculationModal, setShowCalculationModal,
    calculationBreakdownData, setCalculationBreakdownData,
    isCalculationLoading, setIsCalculationLoading,
    showExpressPaymentModal, setShowExpressPaymentModal,
    showExpressValidMessage, setShowExpressValidMessage,
    isExpressCheckboxChecked, setIsExpressCheckboxChecked,
    showCalendar, setShowCalendar,
    activeFreelancerForCalendar, setActiveFreelancerForCalendar,
    showDeductionModal, setShowDeductionModal,
    currentDeductionFreelancerId, setCurrentDeductionFreelancerId,
    editingDeductionIndex, setEditingDeductionIndex,
    editingDeductionData, setEditingDeductionData,
    editingCopiedWorkingHourIndex, setEditingCopiedWorkingHourIndex,
    pensionPay, setPensionPay,
    holidayPay, setHolidayPay,
    expressPayments, setExpressPayments,
    calculatedSubtotal, setCalculatedSubtotal,
    calculatedTotal, setCalculatedTotal,
    vatAmount, setVatAmount
  } = modalState;

  // Destructure dropdown state
  const {
    isShowDatesDropdownOpen, setIsShowDatesDropdownOpen, showDatesDropdownRef,
    isHoursPerDayDropdownOpen, setIsHoursPerDayDropdownOpen, hoursPerDayDropdownRef,
    isWeekOffDaysDropdownOpen, setIsWeekOffDaysDropdownOpen, weekOffDaysDropdownRef
  } = dropdownState;

  // Additional UI state
  const [weekOffDays, setWeekOffDays] = useState<string[]>([]);
  const [serviceDescription, setServiceDescription] = useState<string>('');
  const [serviceDescriptionError, setServiceDescriptionError] = useState<string>('');

  // Deduplicate timesheetData at the top level to prevent duplicate working hours in payload
  const deduplicatedTimesheetData = useMemo(() => {
    if (!timesheetData || timesheetData.length === 0) {
      return [];
    }
    
    const deduplicated = timesheetData.reduce((acc: any[], current: any) => {
      // Check if this item already exists in accumulator
      const isDuplicate = acc.some((item: any) => {
        // Priority 1: If timeSheetWorkingHourId exists and is not 0, use it as PRIMARY unique identifier
        // Same timeSheetWorkingHourId = duplicate, remove it
        if (current.timeSheetWorkingHourId && current.timeSheetWorkingHourId !== 0 && 
            item.timeSheetWorkingHourId && item.timeSheetWorkingHourId !== 0) {
          return item.timeSheetWorkingHourId === current.timeSheetWorkingHourId;
        }
        // Priority 2: If no valid timeSheetWorkingHourId, check by combination
        // Different timeSheetWorkingHourId with same date is VALID (don't remove)
        // Only deduplicate if ALL fields match (userId, workingDate, workingHour, articleNumber)
        if (!current.timeSheetWorkingHourId || current.timeSheetWorkingHourId === 0) {
          return (
            item.userId === current.userId &&
            item.workingDate === current.workingDate &&
            item.workingHour === current.workingHour &&
            item.articleNumber === current.articleNumber
          );
        }
        return false;
      });
      
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, []);
    
    return deduplicated;
  }, [timesheetData]);

  // Filter unique freelancers to prevent duplicate rendering when same userId appears multiple times
  const uniqueFreelancersForInvoice = useMemo(() => {
    // console.log('🔍 [FREELANCER TRACE] Raw selectedFreelancersForInvoice from Redux:', selectedFreelancersForInvoice);
    // console.log('🔍 [FREELANCER TRACE] Length:', selectedFreelancersForInvoice?.length);
    
    if (!selectedFreelancersForInvoice || selectedFreelancersForInvoice.length === 0) {
      // console.log('⚠️ [FREELANCER TRACE] No freelancers found, returning empty array');
      return [];
    }
    
    const seen = new Set<string>();
    const uniqueList: any[] = [];
    
    selectedFreelancersForInvoice.forEach((freelancer: any) => {
      const freelancerUserId = (freelancer.userId || freelancer.id)?.toString();
      
      if (freelancerUserId && !seen.has(freelancerUserId)) {
        seen.add(freelancerUserId);
        uniqueList.push(freelancer);
      }
    });
    
    // If timesheetData is available and URL has timesheet=true parameter, 
    // filter to only show freelancers who have data in timesheetData
    const searchParams = new URLSearchParams(window.location.search);
    const timesheetParam = searchParams.get('timesheet');
    
    if (timesheetParam === 'true' && deduplicatedTimesheetData && deduplicatedTimesheetData.length > 0) {
      // Get unique userIds from timesheetData
      const userIdsInTimesheet = new Set(
        deduplicatedTimesheetData.map((item: any) => item.userId?.toString()).filter(Boolean)
      );
      
      // Filter to only include freelancers that have timesheet data
      const filteredList = uniqueList.filter((freelancer: any) => {
        const freelancerUserId = (freelancer.userId || freelancer.id)?.toString();
        return userIdsInTimesheet.has(freelancerUserId);
      });
      
      // console.log('✅ [FREELANCER TRACE] Filtered list for timesheet mode:', filteredList);
      return filteredList;
    }
    
    // console.log('✅ [FREELANCER TRACE] Final unique freelancers list:', uniqueList);
    return uniqueList;
  }, [selectedFreelancersForInvoice, deduplicatedTimesheetData]);

  useFreelancerExpansion({
    selectedFreelancersForInvoice: uniqueFreelancersForInvoice,
    expandedSections,
    setExpandedSections,
  });

  // Note: Form initialization logic has been moved to useFormInitialization hook
  useFormInitialization({
    copiedInvoiceData,
    setValue,
    watch,
    setTableRowsByFreelancer,
    setCalendarSelectedDatesByFreelancer,
    setPensionDataByFreelancer,
    setHolidayDataByFreelancer,
    setPensionPay,
    setHolidayPay,
    setExpressPayments,
  });

  // Note: Freelancer cleanup logic has been moved to useFreelancerCleanup hook
  useFreelancerCleanup({
    selectedFreelancersForInvoice,
    copiedInvoiceData,
    setTableRowsByFreelancer,
    setServiceTableDataByFreelancer,
    setDeductionsByFreelancer,
    setPensionDataByFreelancer,
    setHolidayDataByFreelancer,
    setCalendarSelectedDatesByFreelancer,
    setLastUsedPricingTypeByFreelancer,
    setExpandedSections,
    setRemovedCopiedWorkingHours,
  });

  // Refs for tracking state changes and preventing race conditions (initialized before use)
  const isInitialMount = React.useRef(true);
  const latestRequestIdRef = React.useRef<number>(0);
  const prevServiceDataRef = React.useRef<string>('');
  const prevCurrencyRef = React.useRef<string>(currency);
  const prevPensionDataRef = React.useRef<string>(JSON.stringify(pensionDataByFreelancer));
  const prevHolidayDataRef = React.useRef<string>(JSON.stringify(holidayDataByFreelancer));
  const prevDeductionsDataRef = React.useRef<string>(JSON.stringify(deductionsByFreelancer));

  useTotalAmountCalculation({ tableRowsByFreelancer, setValue });

  const { pricingType, isHourlyPrice, isDailyPrice, isWeeklyPrice, isMonthlyPrice, isAgreementPrice } = usePricingType({ preferences });
  const { engagementType, isRegularEngagement, isOccasionalEngagement } = useEngagementType({ preferences });

  // Clear week-off days when engagement type changes to occasional
  useEffect(() => {
    if (isOccasionalEngagement) {
      setWeekOffDays([]);
    }
  }, [isOccasionalEngagement]);

  // Re-filter dates when week-off days change - handled by custom hook
  useWeekOffDaysFiltering({
    weekOffDays,
    isRegularEngagement,
    setTableRowsByFreelancer,
    calendarSelectedDatesByFreelancer,
    setCalendarSelectedDatesByFreelancer,
    setValue
  });

  const accommodation = watch('accommodation');
  const foodAllowance = watch('foodAllowance');
  const mileage = watch('mileage');
  const workDates = watch('workDates');
  const showDates = watch('showDates');
  const deductionType = preferences?.deductionType || 'none';
  const isRutOrRotDeduction = deductionType === 'rut' || deductionType === 'rot';
  const vat = typeof preferences?.vat === 'number' ? preferences.vat : (typeof preferences?.vat === 'string' ? parseInt(preferences.vat) : 25);
  const hoursPerDayType = watch('hoursPerDayType');
  const pensionValue = watch('pension');
  const holidaySavings = watch('holidaySavings');
  const expressPayout = watch('expressPayout');
  const workTime = watch('workTime');
  const hourlyPrice = watch('hourlyPrice');

  const prevHourlyPriceRef = React.useRef<string>(hourlyPrice || '');
  const prevVatRef = React.useRef<number>(vat);
  const prevDeductionTypeRef = React.useRef<string>(deductionType);

  const isInitialMountRef = useRef(true);
  
  // Store modal data per freelancer to avoid cross-contamination
  const [modalDataByFreelancer, setModalDataByFreelancer] = useState<Record<string, any>>({});
  const { 
    calculatedSubAmount, 
    isCalculateInvoiceLoading,
    setCalculatedSubAmount 
  } = useInvoiceCalculation({
    serviceTableDataByFreelancer,
    currency,
    deductionType,
    vat,
    hourlyPrice,
    selectedFreelancersForInvoice,
    pensionDataByFreelancer,
    holidayDataByFreelancer,
    deductionsByFreelancer,
    watch,
    calculatedAmounts,
    preferences,
    customerState,
    globalData,
    copiedInvoiceData,
    removedCopiedWorkingHours,
    isInitialMount: isInitialMountRef.current,
    createdDate,
    invoiceNumber // Pass invoiceNumber to the hook
  });

  useParentNotificationsAll({
    serviceTableDataByFreelancer,
    pensionDataByFreelancer,
    holidayDataByFreelancer,
    deductionsByFreelancer,
    removedCopiedWorkingHours,
    calculatedSubAmount,
    onServiceTableDataByFreelancerChange,
    onPensionDataByFreelancerChange,
    onHolidayDataByFreelancerChange,
    onDeductionsByFreelancerChange,
    onRemovedCopiedWorkingHoursChange,
    onCalculatedSubAmountChange,
  });

  useEffect(() => {
    isInitialMountRef.current = false;
  }, []);

  const { accommodationOptions, allowanceOptions, showDatesOptions, hoursPerDayOptions, serviceOptions } = useDropdownOptions({ t, articles });

  const { validateFieldWithTooltip, validateArticleWithPricingTypeAndTooltip, clearValidationErrors } = 
    createFieldValidator(setShowTooltipError || (() => {}));

  const { handleInputChange } = useInputChangeHandler({
    setValue,
    setShowTooltipError,
    tableRowsByFreelancer,
    setTableRowsByFreelancer,
    isHourlyPrice,
    isDailyPrice,
    isWeeklyPrice,
    isMonthlyPrice,
    isAgreementPrice,
    validateArticleWithPricingTypeAndTooltip,
    clearValidationErrors,
    showTooltipError,
  });

  // Note: Express Payment validation logic has been moved to useExpressPaymentValidation hook
  const {
    language,
    expressDe,
    expressEng,
    getExpressPaymentMessage,
    isExpressPaymentAvailable,
    shouldShowWarningMessage,
    handleExpressPaymentCheckbox,
    handleExpressPaymentReadMore,
    handleExpressPaymentModalConfirm,
  } = useExpressPaymentValidation({
    isLoginWithBankId,
    selectedCustomer,
    calculatedSubAmount,
    t,
    setValue,
    setIsExpressCheckboxChecked,
    setShowExpressPaymentModal,
    setShowExpressValidMessage,
    setExpressPayments,
    handleInputChange,
  });

  // Phase 1: Table row handlers hook
  const { handleRowChange, handleRemoveRow, handleAddRow, handleAddWorkingHoursSection } = useTableRowHandlers({
    tableRowsByFreelancer,
    setTableRowsByFreelancer,
    lastUsedPricingTypeByFreelancer,
    setLastUsedPricingTypeByFreelancer,
    calendarSelectedDatesByFreelancer,
    setCalendarSelectedDatesByFreelancer,
    watch,
    hourlyPrice,
    isRutOrRotDeduction,
    serviceDescription
  });

  // Phase 1: Date selection hook
  const { handleDateSelect } = useDateSelection({
    setCalendarSelectedDatesByFreelancer,
    setTableRowsByFreelancer,
    setShowCalendar,
    lastUsedPricingTypeByFreelancer,
    isRegularEngagement,
    weekOffDays,
    isRutOrRotDeduction,
    serviceDescription,
    setValue,
    handleInputChange,
    watch
  });

  // Get stored modal data for a freelancer
  const getModalDataByFreelancer = (freelancerId: string) => {
    const data = modalDataByFreelancer[freelancerId];
    return data ? {
      formData: data.formData,
      selectedDates: data.selectedDates
    } : undefined;
  };

  // Extract dates from tableRowsByFreelancer and populate modalDataByFreelancer when fetching data
  // This works for both: 1) copied invoice data, 2) timesheet data loaded into working hours
  useEffect(() => {
    if (tableRowsByFreelancer && Object.keys(tableRowsByFreelancer).length > 0) {
      const newModalData: Record<string, any> = {};
      
      Object.keys(tableRowsByFreelancer).forEach(freelancerId => {
        const rows = tableRowsByFreelancer[freelancerId];
        if (rows && rows.length > 0) {
          // Extract dates from rows - date is in format "MM/DD/YYYY" or "YYYY-MM-DD"
          const extractedDates = rows
            .filter((row: any) => row.date)
            .map((row: any) => {
              // Parse date string to Date object
              const dateStr = row.date;
              let parsedDate: Date;
              
              // Check if date is in MM/DD/YYYY format
              if (dateStr.includes('/')) {
                const [month, day, year] = dateStr.split('/');
                parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              } else {
                // Assume YYYY-MM-DD format
                parsedDate = new Date(dateStr);
              }
              
              return parsedDate;
            })
            .filter((date: Date) => !isNaN(date.getTime())); // Filter out invalid dates
          
          // Extract form data from first row
          const firstRow = rows[0];
          if (extractedDates.length > 0 && firstRow) {
            newModalData[freelancerId] = {
              formData: {
                hoursPerDay: firstRow.time || '08:00',
                hourlyPrice: firstRow.price?.toString() || '',
                showDates: 'yes',
                weekOffDays: [],
                serviceDescription: firstRow.service || firstRow.customWorkingDescription || '',
                pricingType: firstRow.pricingType || 'Tim',
              },
              selectedDates: extractedDates
            };
          }
        }
      });
      
      // Only update if we found data and modalDataByFreelancer is empty
      if (Object.keys(newModalData).length > 0 && Object.keys(modalDataByFreelancer).length === 0) {
        setModalDataByFreelancer(newModalData);
      }
    }
  }, [tableRowsByFreelancer]);

  // Sync modal dates with table rows whenever rows change (add/remove)
  useEffect(() => {
    if (tableRowsByFreelancer && Object.keys(tableRowsByFreelancer).length > 0) {
      setModalDataByFreelancer(prev => {
        const updated = { ...prev };
        
        Object.keys(tableRowsByFreelancer).forEach(freelancerId => {
          const rows = tableRowsByFreelancer[freelancerId];
          
          // Extract dates from current rows
          const extractedDates = rows
            .filter((row: any) => row.date)
            .map((row: any) => {
              const dateStr = row.date;
              let parsedDate: Date;
              
              if (dateStr.includes('/')) {
                const [month, day, year] = dateStr.split('/');
                parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              } else {
                parsedDate = new Date(dateStr);
              }
              
              return parsedDate;
            })
            .filter((date: Date) => !isNaN(date.getTime()));
          
          // If this freelancer has existing modal data, update only the dates
          if (prev[freelancerId]) {
            updated[freelancerId] = {
              ...prev[freelancerId],
              selectedDates: extractedDates
            };
          }
        });
        
        return updated;
      });
    }
  }, [tableRowsByFreelancer]);

  // Handler for auto-generate modal
  const handleAutoGenerateItems = (freelancerId: string, modalData: any, selectedDates: Date[]) => {
    // Store modal data per freelancer for reference
    setModalDataByFreelancer(prev => ({
      ...prev,
      [freelancerId]: {
        formData: modalData,
        selectedDates: selectedDates
      }
    }));
    
    // Generate table rows using handleDateSelect with freelancer-specific overrides
    // This prevents data from one freelancer affecting another
    if (selectedDates.length > 0) {
      handleDateSelect(
        freelancerId,
        selectedDates[0],
        selectedDates[selectedDates.length - 1],
        selectedDates,
        {
          hoursPerDay: modalData.hoursPerDay,
          hourlyPrice: modalData.hourlyPrice,
          serviceDescription: modalData.serviceDescription,
          weekOffDays: modalData.weekOffDays,
          pricingType: modalData.pricingType
        }
      );
    }
  };

  // Handler for removing freelancer from invoice
  const handleRemoveFreelancer = (freelancerId: string) => {
    // Prevent removal of the freelancer at index 0
    if (selectedFreelancersForInvoice.length > 0 && freelancerId === (selectedFreelancersForInvoice[0].userId?.toString() || selectedFreelancersForInvoice[0].id?.toString())) {
      return;
    }
    // Remove freelancer from the selected list
    const updatedFreelancers = selectedFreelancersForInvoice.filter(
      (f: any) => (f.userId?.toString() || f.id?.toString()) !== freelancerId
    );
    dispatch(setSelectedFreelancersForInvoice(updatedFreelancers));
    
    // Clean up all related data for this freelancer
    setTableRowsByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setServiceTableDataByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setDeductionsByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setPensionDataByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setHolidayDataByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setCalendarSelectedDatesByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setModalDataByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
  };

  // Phase 1: Deduction submit hook
  const { handleDeductionSubmit } = useDeductionSubmit({
    currentDeductionFreelancerId,
    editingDeductionIndex,
    deductionsByFreelancer,
    setDeductionsByFreelancer,
    serviceTableDataByFreelancer,
    pensionDataByFreelancer,
    holidayDataByFreelancer,
    editingCopiedWorkingHourIndex,
    setEditingCopiedWorkingHourIndex,
    setRemovedCopiedWorkingHours,
    selectedFreelancersForInvoice,
    calculatedAmounts,
    copiedInvoiceData,
    removedCopiedWorkingHours,
    latestRequestIdRef,
    setCalculatedSubAmount,
    preferences,
    vat,
    currency,
    globalData,
    deductionType,
    customerState,
    watch,
    dispatch,
    createdDate,
    invoiceNumber // Pass invoiceNumber to the hook
  });

  // Phase 1: Calculation breakdown hook
  const { handleShowCalculationBreakdown } = useCalculationBreakdown({
    setIsCalculationLoading,
    setCalculationBreakdownData,
    setShowCalculationModal,
    selectedFreelancersForInvoice,
    serviceTableDataByFreelancer,
    deductionsByFreelancer,
    pensionDataByFreelancer,
    holidayDataByFreelancer,
    copiedInvoiceData,
    removedCopiedWorkingHours,
    calculatedAmounts,
    preferences,
    globalData,
    customerState,
    watch,
    dispatch,
    createdDate
  });

  // Phase 2: Effect hooks for table row updates and calculations
  useTableRowUpdates({
    hoursPerDayType: watch('hoursPerDayType') || '',
    hourlyPrice,
    serviceDescription,
    deductionType,
    tableRowsByFreelancer,
    setTableRowsByFreelancer,
    convertToDecimalHours,
    convertToTimeFormat
  });

  useBenefitsManagement({
    isOccasionalEngagement,
    engagementType,
    pensionValue,
    holidaySavings,
    setValue,
    setPensionPay,
    setHolidayPay
  });

  const { handleCalendarOpen, handleCalendarClose, handleOpenDeductionModal, handleCloseDeductionModal, toggleSection } = useModalHandlers({
    setActiveFreelancerForCalendar,
    setShowCalendar,
    setCurrentDeductionFreelancerId,
    setEditingDeductionIndex,
    setEditingDeductionData,
    setShowDeductionModal,
    setEditingCopiedWorkingHourIndex,
    deductionsByFreelancer,
    setExpandedSections,
    setValue,
    setShowTooltipError,
  });

  // handleRowChange, handleRemoveRow, handleAddRow, handleAddWorkingHoursSection
  // are now provided by useTableRowHandlers hook

  return (
    <Card className="invoice-card mb-4">
      <Card.Header className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Card.Title as="h3">{t('ITEM_DETAILS')}</Card.Title>
        {(!selectedFreelancersForInvoice || selectedFreelancersForInvoice.length === 0) && (
          <Tooltip content={t('SELECT_CUSTOMER_FIRST_TOOLTIP')} position="bottom">
            <button
              disabled
              style={{
                padding: '6px 12px',
                fontSize: '14px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                background: '#f3f4f6',
                color: '#9ca3af',
                cursor: 'not-allowed',
                opacity: 0.6
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M7.69053 1.39112C7.80522 1.11963 8.18996 1.11963 8.30465 1.39112L10.1228 5.69503C10.1566 5.77497 10.2202 5.8386 10.3002 5.87237L14.6041 7.69053C14.8756 7.80522 14.8756 8.18996 14.6041 8.30465L10.3002 10.1228C10.2202 10.1566 10.1566 10.2202 10.1228 10.3002L8.30465 14.6041C8.18996 14.8756 7.80522 14.8756 7.69053 14.6041L5.87237 10.3002C5.8386 10.2202 5.77497 10.1566 5.69503 10.1228L1.39112 8.30465C1.11963 8.18996 1.11963 7.80522 1.39112 7.69053L5.69503 5.87237C5.77497 5.8386 5.8386 5.77497 5.87237 5.69503L7.69053 1.39112Z" stroke="#9ca3af" strokeWidth="2"/>
              </svg>
              {t('AUTO_GENERATE_ITEMS') || 'Auto Generate Items'}
            </button>
          </Tooltip>
        )}
      </Card.Header>
      <Card.Body className="card-body">
        <div className="item-details-container">

          {/* Display message if no freelancers are selected */}
          {!selectedFreelancersForInvoice || selectedFreelancersForInvoice.length === 0 ? (
            <DefaultInvoiceSection
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              trigger={trigger}
              isRegularEngagement={isRegularEngagement}
              currency={currency}
              isHourlyPrice={isHourlyPrice}
              isDailyPrice={isDailyPrice}
              isWeeklyPrice={isWeeklyPrice}
              isMonthlyPrice={isMonthlyPrice}
              deductionType={deductionType}
              hoursPerDayType={hoursPerDayType}
              workDates={workDates}
              showDates={showDates}
              selectedCustomer={selectedCustomer}
              isHoursPerDayDropdownOpen={isHoursPerDayDropdownOpen}
              isShowDatesDropdownOpen={isShowDatesDropdownOpen}
              isWeekOffDaysDropdownOpen={isWeekOffDaysDropdownOpen}
              showDatesDropdownRef={showDatesDropdownRef}
              weekOffDaysDropdownRef={weekOffDaysDropdownRef}
              weekOffDays={weekOffDays}
              setWeekOffDays={setWeekOffDays}
              showCalendar={showCalendar}
              activeFreelancerForCalendar={activeFreelancerForCalendar}
              calendarSelectedDatesByFreelancer={calendarSelectedDatesByFreelancer}
              tableRowsByFreelancer={tableRowsByFreelancer}
              serviceTableDataByFreelancer={serviceTableDataByFreelancer}
              deductionsByFreelancer={deductionsByFreelancer}
              pensionDataByFreelancer={pensionDataByFreelancer}
              holidayDataByFreelancer={holidayDataByFreelancer}
              copiedInvoiceData={copiedInvoiceData}
              removedCopiedWorkingHours={removedCopiedWorkingHours}
              hoursPerDayOptions={hoursPerDayOptions}
              showDatesOptions={showDatesOptions}
              serviceOptions={serviceOptions}
              showTooltipError={showTooltipError}
              handleInputChange={handleInputChange}
              handleCalendarOpen={handleCalendarOpen}
              handleCalendarClose={handleCalendarClose}
              handleDateSelect={handleDateSelect}
              setIsHoursPerDayDropdownOpen={setIsHoursPerDayDropdownOpen}
              setIsShowDatesDropdownOpen={setIsShowDatesDropdownOpen}
              setIsWeekOffDaysDropdownOpen={setIsWeekOffDaysDropdownOpen}
              handleRowChange={handleRowChange}
              handleRemoveRow={handleRemoveRow}
              handleAddWorkingHoursSection={handleAddWorkingHoursSection}
              setServiceTableDataByFreelancer={setServiceTableDataByFreelancer}
              handleOpenDeductionModal={handleOpenDeductionModal}
              setEditingDeductionData={setEditingDeductionData}
              setCurrentDeductionFreelancerId={setCurrentDeductionFreelancerId}
              setEditingDeductionIndex={setEditingDeductionIndex}
              setShowDeductionModal={setShowDeductionModal}
              setRemovedCopiedWorkingHours={setRemovedCopiedWorkingHours}
              setDeductionsByFreelancer={setDeductionsByFreelancer}
              setPensionDataByFreelancer={setPensionDataByFreelancer}
              setHolidayDataByFreelancer={setHolidayDataByFreelancer}
              globalData={globalData}
              preferences={preferences}
              validateServiceDescriptionsRef={validateServiceDescriptionsRef}
              t={t}
            />
          ) : (
            // Display each selected freelancer's details using FreelancerCardContainer component
            // Using uniqueFreelancersForInvoice to prevent duplicate rendering of same userId
            uniqueFreelancersForInvoice.map((friend: any, index: number) => {
              // console.log(`📋 [FREELANCER RENDER] Rendering freelancer at index ${index}:`, {
              //   userId: friend.userId,
              //   id: friend.id,
              //   name: friend.name,
              //   email: friend.email,
              //   fullData: friend
              // });
              
              // Filter timesheetData for THIS SPECIFIC freelancer only
              // This ensures each freelancer card ONLY receives their own timesheet data
              const freelancerUserId = (friend.userId || friend.id)?.toString();
              
              // Filter deduplicatedTimesheetData to only include entries for this specific freelancer
              const freelancerSpecificTimesheetData = deduplicatedTimesheetData?.filter((item: any) => {
                const itemUserId = item.userId?.toString();
                return itemUserId === freelancerUserId;
              }) || [];
              
              return (
              <FreelancerCardContainer
                key={friend.userId || friend.id}
                friend={friend}
                index={index}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                register={register}
                setValue={setValue}
                watch={watch}
                errors={errors}
                trigger={trigger}
                isRegularEngagement={isRegularEngagement}
                currency={currency}
                isHourlyPrice={isHourlyPrice}
                isDailyPrice={isDailyPrice}
                isWeeklyPrice={isWeeklyPrice}
                isMonthlyPrice={isMonthlyPrice}
                deductionType={deductionType}
                hoursPerDayType={hoursPerDayType}
                workDates={workDates}
                showDates={showDates}
                weekOffDays={weekOffDays}
                serviceDescription={serviceDescription}
                serviceDescriptionError={serviceDescriptionError}
                isShowDatesDropdownOpen={isShowDatesDropdownOpen}
                isWeekOffDaysDropdownOpen={isWeekOffDaysDropdownOpen}
                showDatesDropdownRef={showDatesDropdownRef}
                weekOffDaysDropdownRef={weekOffDaysDropdownRef}
                showCalendar={showCalendar}
                activeFreelancerForCalendar={activeFreelancerForCalendar}
                calendarSelectedDatesByFreelancer={calendarSelectedDatesByFreelancer}
                tableRowsByFreelancer={tableRowsByFreelancer}
                serviceTableDataByFreelancer={serviceTableDataByFreelancer}
                deductionsByFreelancer={deductionsByFreelancer}
                pensionDataByFreelancer={pensionDataByFreelancer}
                holidayDataByFreelancer={holidayDataByFreelancer}
                copiedInvoiceData={copiedInvoiceData}
                removedCopiedWorkingHours={removedCopiedWorkingHours}
                calculatedSubAmount={calculatedSubAmount}
                isCalculateInvoiceLoading={isCalculateInvoiceLoading}
                serviceOptions={serviceOptions}
                showDatesOptions={showDatesOptions}
                showTooltipError={showTooltipError}
                handleInputChange={handleInputChange}
                handleCalendarOpen={handleCalendarOpen}
                handleCalendarClose={handleCalendarClose}
                handleDateSelect={handleDateSelect}
                onAutoGenerateItems={handleAutoGenerateItems}
                getModalDataByFreelancer={getModalDataByFreelancer}
                onRemoveFreelancer={handleRemoveFreelancer}
                setIsShowDatesDropdownOpen={setIsShowDatesDropdownOpen}
                setIsWeekOffDaysDropdownOpen={setIsWeekOffDaysDropdownOpen}
                setWeekOffDays={setWeekOffDays}
                setServiceDescription={setServiceDescription}
                setServiceDescriptionError={setServiceDescriptionError}
                handleRowChange={handleRowChange}
                handleRemoveRow={handleRemoveRow}
                handleAddWorkingHoursSection={handleAddWorkingHoursSection}
                setServiceTableDataByFreelancer={setServiceTableDataByFreelancer}
                handleOpenDeductionModal={handleOpenDeductionModal}
                setEditingDeductionData={setEditingDeductionData}
                setCurrentDeductionFreelancerId={setCurrentDeductionFreelancerId}
                setEditingDeductionIndex={setEditingDeductionIndex}
                setEditingCopiedWorkingHourIndex={setEditingCopiedWorkingHourIndex}
                setShowDeductionModal={setShowDeductionModal}
                timesheetData={freelancerSpecificTimesheetData}
                setRemovedCopiedWorkingHours={setRemovedCopiedWorkingHours}
                setDeductionsByFreelancer={setDeductionsByFreelancer}
                setPensionDataByFreelancer={setPensionDataByFreelancer}
                setHolidayDataByFreelancer={setHolidayDataByFreelancer}
                globalData={globalData}
                preferences={preferences}
                validateServiceDescriptionsRef={validateServiceDescriptionsRef}
                t={t}
                selectedFreelancersForInvoice={selectedFreelancersForInvoice}
                calculatedAmounts={calculatedAmounts}
                customerState={customerState}
                dispatch={dispatch}
              />
              );
            })
          )}

          {/* Express Payments Section */}
          <ExpressPaymentSection
            selectedCustomer={selectedCustomer}
            selectedFreelancersForInvoice={selectedFreelancersForInvoice}
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            isExpressCheckboxChecked={isExpressCheckboxChecked}
            isEditModeInvoice={isEditModeInvoice || false}
            handleExpressPaymentCheckbox={handleExpressPaymentCheckbox}
            handleExpressPaymentReadMore={handleExpressPaymentReadMore}
            isExpressPaymentAvailable={isExpressPaymentAvailable}
            getExpressPaymentMessage={getExpressPaymentMessage}
            isCalculateInvoiceLoading={isCalculateInvoiceLoading}
            calculatedSubAmount={calculatedSubAmount}
            currency={currency}
            language={i18n.language}
            vat={vat}
            vatAmount={vatAmount}
            isCalculationLoading={isCalculationLoading}
            handleShowCalculationBreakdown={handleShowCalculationBreakdown}
            t={t}
          />
        </div>
      </Card.Body>

      <InvoiceModals
        showDeductionModal={showDeductionModal}
        showCalculationModal={showCalculationModal}
        showExpressPaymentModal={showExpressPaymentModal}
        handleCloseDeductionModal={handleCloseDeductionModal}
        handleDeductionSubmit={handleDeductionSubmit}
        setShowCalculationModal={setShowCalculationModal}
        setCalculationBreakdownData={setCalculationBreakdownData}
        setShowExpressPaymentModal={setShowExpressPaymentModal}
        handleExpressPaymentModalConfirm={handleExpressPaymentModalConfirm}
        register={register}
        setValue={setValue}
        watch={watch}
        errors={errors}
        trigger={trigger}
        t={t}
        editingDeductionData={editingDeductionData}
        copiedInvoiceData={copiedInvoiceData}
        calculationBreakdownData={calculationBreakdownData}
        currency={currency}
        calculatedSubAmount={calculatedSubAmount}
        preferences={preferences}
      />
    </Card>
  );
};

export default ItemDetails;